import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { cars } from '../data/cars';
import {
  LOAN_DEFAULTS,
  MIN_LOAN_AMOUNT,
  TENURE_OPTIONS,
  calculateLoan,
  formatRM,
  getDownPaymentFromPercent,
  getSliderMaxDownPercent,
  parsePrice,
} from '../utils/loanCalculator';

const LoanCalculator = () => {
  const [searchParams] = useSearchParams();

  const initialCarId = searchParams.get('car');
  const initialVariant = parseInt(searchParams.get('variant') ?? '0', 10);

  const validCar = cars.find((c) => c.id === initialCarId) || cars[0];
  const safeVariantIndex =
    Number.isFinite(initialVariant) &&
    initialVariant >= 0 &&
    initialVariant < validCar.variants.length
      ? initialVariant
      : 0;

  const [carId, setCarId] = useState(validCar.id);
  const [variantIndex, setVariantIndex] = useState(safeVariantIndex);
  const [useCustomPrice, setUseCustomPrice] = useState(false);
  const [customPrice, setCustomPrice] = useState('');
  const [downPercent, setDownPercent] = useState(LOAN_DEFAULTS.downPercent);
  const [tenureYears, setTenureYears] = useState(LOAN_DEFAULTS.tenureYears);
  const [annualRate, setAnnualRate] = useState(LOAN_DEFAULTS.annualRate);

  const selectedCar = useMemo(
    () => cars.find((c) => c.id === carId) || cars[0],
    [carId]
  );

  const variants = selectedCar.variants || [];
  const selectedVariant = variants[variantIndex] || variants[0];

  const vehiclePrice = useCustomPrice
    ? parsePrice(customPrice)
    : parsePrice(selectedVariant?.price);

  const maxDownPercent = useMemo(
    () => getSliderMaxDownPercent(vehiclePrice),
    [vehiclePrice]
  );

  const loanResult = useMemo(
    () =>
      calculateLoan({
        price: vehiclePrice,
        downPercent,
        tenureYears,
        annualRate,
      }),
    [vehiclePrice, downPercent, tenureYears, annualRate]
  );

  useEffect(() => {
    const paramCar = searchParams.get('car');
    const paramVariant = parseInt(searchParams.get('variant') ?? '0', 10);
    if (paramCar) {
      const car = cars.find((c) => c.id === paramCar);
      if (car) {
        setCarId(car.id);
        const idx =
          Number.isFinite(paramVariant) &&
          paramVariant >= 0 &&
          paramVariant < car.variants.length
            ? paramVariant
            : 0;
        setVariantIndex(idx);
        setUseCustomPrice(false);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (downPercent > maxDownPercent) {
      setDownPercent(maxDownPercent);
    }
  }, [vehiclePrice, maxDownPercent, downPercent]);

  useEffect(() => {
    if (window.location.hash === '#calculator') {
      const el = document.getElementById('calculator');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, [searchParams]);

  const handleCarChange = (e) => {
    setCarId(e.target.value);
    setVariantIndex(0);
    setUseCustomPrice(false);
  };

  const handleVariantChange = (e) => {
    setVariantIndex(parseInt(e.target.value, 10));
    setUseCustomPrice(false);
  };

  const downPaymentAmount = getDownPaymentFromPercent(vehiclePrice, downPercent);
  const effectiveDownPercent =
    vehiclePrice > 0 ? (downPaymentAmount / vehiclePrice) * 100 : 0;

  return (
    <section id="calculator" className="section">
      <h2 className="section-title">Loan Calculator</h2>
      <p style={{ textAlign: 'center', marginTop: '-30px', marginBottom: '30px', color: '#666' }}>
        Estimate your monthly installment.
      </p>

      <div className="calculator-section">
        <div className="calc-grid">
          <div className="calc-inputs">
            <div className="form-group">
              <label>Model</label>
              <select value={carId} onChange={handleCarChange}>
                {cars.map((car) => (
                  <option key={car.id} value={car.id}>
                    Perodua {car.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Variant</label>
              <select
                value={variantIndex}
                onChange={handleVariantChange}
                disabled={useCustomPrice || variants.length === 0}
              >
                {variants.map((v, i) => (
                  <option key={i} value={i}>
                    {v.name} — {v.price}
                  </option>
                ))}
              </select>
            </div>

            {/* <div className="form-group calc-checkbox-row">
              <label>
                <input
                  type="checkbox"
                  checked={useCustomPrice}
                  onChange={(e) => setUseCustomPrice(e.target.checked)}
                />
                {' '}Custom price (promo / special)
              </label>
            </div> */}

            {useCustomPrice && (
              <div className="form-group">
                <label>Vehicle price (RM)</label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  placeholder="e.g. 45000"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                />
              </div>
            )}

            <div className="form-group">
              <label>
                Down payment ({effectiveDownPercent.toFixed(1)}%)
              </label>
              <input
                type="range"
                min="0"
                max={maxDownPercent}
                step="1"
                value={downPercent}
                onChange={(e) => setDownPercent(Number(e.target.value))}
              />
              <span className="calc-hint">
                {formatRM(downPaymentAmount)} down · Loan {formatRM(loanResult.loanAmount)}
              </span>
            </div>

            <div className="form-group">
              <label>Loan tenure</label>
              <select
                value={tenureYears}
                onChange={(e) => setTenureYears(Number(e.target.value))}
              >
                {TENURE_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y} years ({y * 12} months)
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Interest rate (% p.a.)</label>
              <input
                type="number"
                min="0"
                max="15"
                step="0.1"
                value={annualRate}
                onChange={(e) => setAnnualRate(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="result-box">
            <p style={{ marginBottom: '8px', opacity: 0.9 }}>Estimated monthly payment</p>
            {loanResult.valid && vehiclePrice > 0 ? (
              <>
                <div className="monthly-payment">
                  {formatRM(loanResult.monthlyPayment)}
                  <span className="monthly-suffix">/mth</span>
                </div>
                <div className="calc-summary">
                  <p>Loan amount: <strong>{formatRM(loanResult.loanAmount)}</strong></p>
                  <p>Total interest: <strong>{formatRM(loanResult.totalInterest)}</strong></p>
                  <p>Total payable: <strong>{formatRM(loanResult.totalPayable)}</strong></p>
                </div>
              </>
            ) : (
              <div className="monthly-payment">—</div>
            )}

            <p className="calc-disclaimer">
              Estimates only. Actual rate and approval depend on bank/finance company.
              OTR price excludes insurance.
            </p>

            <div className="calc-cta">
              <Link to="/#advisors" className="cta-btn">
                Book with Sales Team
              </Link>
              <Link
                to="/#contact"
                state={{ model: selectedCar.name }}
                className="cta-btn calc-cta-secondary"
              >
                Get Quote
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoanCalculator;
